
import { LGraphNode, LiteGraph } from "litegraph.js";
import { GlobalConfig } from "../GlobalConfig.js";

export class DrawingInputNode extends LGraphNode {
  constructor() {
    super();

    // Basic node setup
    this.title = "Drawing Input";
   
    this.canvas = document.createElement("canvas");
    this.canvas.width = 800;
    this.canvas.height = 800;
    this.ctx = this.canvas.getContext("2d");
    this.strokeWidth = parseInt(this.getUrlParam("strokeWidth")) || 50;
    this.history = [];

    this.strokes = JSON.parse(this.getUrlParam("strokes")) || [];
    this.currentStroke = [];

    this.brushColor = GlobalConfig.brushColor; 

    // Optional: fill background with white (comment out if you want transparency)
    this.ctx.fillStyle = "transparent";
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // State for drawing
    this.isDrawing = false;
    this.lastPos = null;

    this.addWidget("button", "Undo", null, () => this.undo());
    this.addWidget("button", "Clear", null, () => this.clearCanvas());

    this.addWidget("slider", "Stroke Width", this.strokeWidth, (val) => {
      this.strokeWidth = val;
      this.updateUrlParam("strokeWidth", val);
    }, { min: 1, max: 100, step: 1 });


    // The node outputs this canvas
    this.addOutput("Canvas", "canvas");

    this.size = [200, 260];
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";

    GlobalConfig.events.addEventListener("brushColor", (event) => {
      this.brushColor = event.detail;
    });

    this.redrawAllStrokes();

  }

  undo() {
    if (this.history.length > 0) {
      const previousState = this.history.pop();
      const img = new Image();
      img.src = previousState;
      img.onload = () => {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.drawImage(img, 0, 0);
        this.updateUrlParam("drawing", previousState);
      };
    }
  }

  redrawAllStrokes() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.strokes.forEach(stroke => {
      this.ctx.strokeStyle = stroke.color;
      this.ctx.lineWidth = stroke.width;
      this.ctx.beginPath();
      stroke.points.forEach((point, index) => {
        if (index === 0) {
          this.ctx.moveTo(point.x, point.y);
        } else {
          this.ctx.lineTo(point.x, point.y);
        }
      });
      this.ctx.stroke();
    });
  }


  saveState() {
    const state = this.canvas.toDataURL();
    this.history.push(state);
    if (this.history.length > 20) {
      this.history.shift();
    }
    this.updateUrlParam("drawing", state);
  }

  clearCanvas() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.history = [];
    this.currentStroke = [];
    this.strokes = [];
    this.updateUrlParam("drawing", "");
    
  }


  getUrlParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  updateUrlParam() {}

  /**
   * Draws the preview inside the node. We scale down the 200×200 internal canvas
   * to fit into the node box.
   */
  onDrawForeground(ctx, graphcanvas) {
    const topOffset = 90; // below widgets
    const pad = 6;
    const previewX = pad;
    const previewY = topOffset;
    const previewW = this.size[0] - pad * 2;
    const previewH = this.size[1] - topOffset - pad;

    // Draw area background
    ctx.fillStyle = "rgba(0,0,0,0.2)";
    ctx.fillRect(previewX, previewY, previewW, previewH);

    const scaleX = previewW / this.canvas.width;
    const scaleY = previewH / this.canvas.height;
    const scale = Math.min(scaleX, scaleY);
    const dw = this.canvas.width * scale;
    const dh = this.canvas.height * scale;
    const dx = previewX + (previewW - dw) / 2;
    const dy = previewY + (previewH - dh) / 2;

    ctx.save();
    ctx.beginPath();
    ctx.rect(previewX, previewY, previewW, previewH);
    ctx.clip();
    ctx.drawImage(this.canvas, dx, dy, dw, dh);
    ctx.restore();
  }

  /**
   * If the user clicks in our preview area, begin drawing.
   */


  _getDrawArea() {
    const topOffset = 90;
    const pad = 6;
    const previewW = this.size[0] - pad * 2;
    const previewH = this.size[1] - topOffset - pad;
    const scaleX = previewW / this.canvas.width;
    const scaleY = previewH / this.canvas.height;
    const scale = Math.min(scaleX, scaleY);
    const dw = this.canvas.width * scale;
    const dh = this.canvas.height * scale;
    const dx = pad + (previewW - dw) / 2;
    const dy = topOffset + (previewH - dh) / 2;
    return { dx, dy, dw, dh, scale };
  }

  onMouseDown(e, pos, graphcanvas) {
    const [mx, my] = pos;
    const { dx, dy, dw, dh, scale } = this._getDrawArea();

    if (mx >= dx && mx <= dx + dw && my >= dy && my <= dy + dh) {
      this.isDrawing = true;
      this.currentStroke = {
        color: this.brushColor,
        width: this.strokeWidth,
        points: [{ x: (mx - dx) / scale, y: (my - dy) / scale }]
      };
    }
    return true;
  }

  onMouseMove(e, pos, graphcanvas) {
    if (!this.isDrawing) return false;

    const [mx, my] = pos;
    const { dx, dy, scale } = this._getDrawArea();

    const currX = (mx - dx) / scale;
    const currY = (my - dy) / scale;

    this.currentStroke.points.push({ x: currX, y: currY });

    this.ctx.strokeStyle = this.brushColor;
    this.ctx.lineWidth = this.strokeWidth;
    this.ctx.beginPath();
    const len = this.currentStroke.points.length;
    if (len > 1) {
      const last = this.currentStroke.points[len - 2];
      this.ctx.moveTo(last.x, last.y);
      this.ctx.lineTo(currX, currY);
      this.ctx.stroke();
    }

    graphcanvas.draw(true);
    return true;
  }

  onMouseUp(e, pos, graphcanvas) {
    if (typeof this.currentStroke.points !== 'undefined' && this.currentStroke.points.length > 1) {
      this.strokes.push(this.currentStroke);
      this.updateUrlParam("strokes", JSON.stringify(this.strokes));
    }
    
    this.isDrawing = false;
    this.currentStroke = [];
    return false;
  }

  /**
   * During graph execution, output the canvas.
   */
  onExecute() {
    const trimmed = trimTransparentPixels(this.canvas);
    this.setOutputData(0, trimmed);
  }
}


function trimTransparentPixels(sourceCanvas) {

  const sourceCtx = sourceCanvas.getContext("2d");
  const { width, height } = sourceCanvas;

  const imageData = sourceCtx.getImageData(0, 0, width, height);
  const data = imageData.data;

  // Initialize bounding box to something impossible
  let minX = width, minY = height, maxX = -1, maxY = -1;

  // Find the bounding box of all non-transparent pixels (alpha > 0)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;      // 4 bytes per pixel
      const alpha = data[idx + 3];         // RGBA => alpha is the 4th component
      if (alpha !== 0) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  // If maxX == -1, it means the entire canvas is empty/transparent
  if (maxX === -1) {
    // No non-transparent pixels; just return the original or an empty canvas
    return sourceCanvas;
  }

  // Dimensions of the cropped area
  const croppedWidth = maxX - minX + 1;
  const croppedHeight = maxY - minY + 1;

  // Create a new canvas
  const trimmedCanvas = document.createElement("canvas");
  trimmedCanvas.width = croppedWidth;
  trimmedCanvas.height = croppedHeight;

  // Draw from the sourceCanvas only the bounding box area
  const trimmedCtx = trimmedCanvas.getContext("2d");
  trimmedCtx.drawImage(
    sourceCanvas,
    minX,
    minY,
    croppedWidth,
    croppedHeight,
    0,
    0,
    croppedWidth,
    croppedHeight
  );

  return trimmedCanvas;
}


// Register the node with LiteGraph
LiteGraph.registerNodeType("draw/DrawingInput", DrawingInputNode);
